import { Popover } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import dayjs, { type Dayjs } from 'dayjs';
import { forwardRef, useMemo, useState } from 'react';
import { Calendar } from '../../../foundations/icons';
import { radius } from '../../../theme/tokens/primitives';
import { VyThemeProvider } from '../../../theme-provider';
import { VyButton } from '../../buttons/VyButton';

const DISPLAY_FORMAT = 'MMM YYYY';
const PLACEHOLDER_START = 'MMM YYYY';
const PLACEHOLDER_END = 'MMM YYYY';
const SEPARATOR = ' – ';
const ICON_SIZE = 16;

export type MonthYearRange = [Dayjs | null, Dayjs | null];

export type VyMonthYearRangePickerProps = {
  value: MonthYearRange;
  /**
 * @tier molecule
 *
 * Fires on Apply or shortcut click — not on every in-popover pick.
 */
  onChange: (range: MonthYearRange) => void;
  /** Inclusive lower bound. */
  minDate?: Dayjs;
  /** Inclusive upper bound. */
  maxDate?: Dayjs;
  /** Shortcut for `maxDate = today`. */
  disableFuture?: boolean;
  disabled?: boolean;
  /** Cypress selector hook. */
  'data-vy'?: string;
};

const FieldRoot = styled('button', {
  shouldForwardProp: (prop) => prop !== 'isEmpty',
})<{ isEmpty: boolean }>(({ theme, isEmpty }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  paddingInline: theme.spacing(1.5),
  paddingBlock: theme.spacing(1),
  height: 40,
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  color: isEmpty ? theme.palette.text.secondary : theme.palette.text.primary,
  fontFamily: theme.typography.fontFamily,
  fontSize: theme.typography.body2?.fontSize,
  fontWeight: theme.typography.fontWeightRegular,
  cursor: 'pointer',
  transition: theme.transitions.create(['border-color', 'background-color'], {
    duration: theme.transitions.duration.shortest,
  }),
  '&:hover': {
    borderColor: theme.palette.text.secondary,
  },
  '&:focus-visible': {
    outline: 'none',
    borderColor: theme.palette.primary.main,
  },
  '&:disabled': {
    cursor: 'not-allowed',
    color: theme.palette.action.disabled,
    backgroundColor: theme.palette.action.disabledBackground,
  },
}));

const PopoverContent = styled('div')({
  display: 'flex',
});

const ShortcutsPanel = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  borderRight: `1px solid ${theme.palette.divider}`,
}));

// `radius.full` is the documented exception from theme.shape (chips = pill).
const ShortcutButton = styled('button')(({ theme }) => ({
  border: 'none',
  whiteSpace: 'nowrap',
  padding: `${theme.spacing(0.75)} ${theme.spacing(1.75)}`,
  borderRadius: radius.full,
  fontFamily: theme.typography.fontFamily,
  fontSize: theme.typography.body2?.fontSize,
  fontWeight: theme.typography.fontWeightMedium,
  color: theme.palette.text.primary,
  backgroundColor: alpha(theme.palette.text.primary, 0.08),
  cursor: 'pointer',
  transition: 'background-color 120ms ease',
  '&:hover': {
    backgroundColor: alpha(theme.palette.text.primary, 0.14),
  },
  '&:focus-visible': {
    outline: 'none',
    backgroundColor: alpha(theme.palette.text.primary, 0.14),
  },
}));

const RightSide = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(1),
  gap: theme.spacing(1),
}));

const CalendarsRow = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
}));

const ActionsRow = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: theme.spacing(1),
  paddingInline: theme.spacing(1),
  paddingBottom: theme.spacing(0.5),
}));

function formatRange(value: MonthYearRange): string {
  const [start, end] = value;
  const startLabel = start ? start.format(DISPLAY_FORMAT) : PLACEHOLDER_START;
  const endLabel = end ? end.format(DISPLAY_FORMAT) : PLACEHOLDER_END;
  return `${startLabel}${SEPARATOR}${endLabel}`;
}

type Shortcut = { label: string; getValue: () => MonthYearRange };

const SHORTCUTS: readonly Shortcut[] = [
  {
    label: 'This year',
    getValue: () => {
      const now = dayjs();
      return [now.startOf('year'), now.startOf('month')];
    },
  },
  {
    label: 'Last year',
    getValue: () => {
      const lastYear = dayjs().subtract(1, 'year');
      return [lastYear.startOf('year'), lastYear.endOf('year').startOf('month')];
    },
  },
  {
    label: 'Last 12 months',
    getValue: () => {
      const now = dayjs().startOf('month');
      return [now.subtract(11, 'month'), now];
    },
  },
  {
    label: 'This month',
    getValue: () => {
      const m = dayjs().startOf('month');
      return [m, m];
    },
  },
  {
    label: 'Last month',
    getValue: () => {
      const m = dayjs().subtract(1, 'month').startOf('month');
      return [m, m];
    },
  },
  {
    label: 'Past 6 months',
    getValue: () => {
      const now = dayjs().startOf('month');
      return [now.subtract(5, 'month'), now];
    },
  },
];

export const VyMonthYearRangePicker = forwardRef<HTMLButtonElement, VyMonthYearRangePickerProps>(
  function VyMonthYearRangePicker(
    { value, onChange, minDate, maxDate, disableFuture, disabled, 'data-vy': dataVy },
    ref,
  ) {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState<MonthYearRange>(value);

    const setRefs = (node: HTMLButtonElement | null) => {
      setAnchorEl(node);
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    };

    const effectiveMaxDate = useMemo(() => {
      if (!disableFuture) return maxDate;
      const today = dayjs().startOf('month');
      return maxDate && maxDate.isBefore(today, 'month') ? maxDate : today;
    }, [disableFuture, maxDate]);

    const isEmpty = !value[0] && !value[1];

    const handleOpen = () => {
      if (disabled) return;
      setDraft(value);
      setOpen(true);
    };

    const handleClose = () => setOpen(false);

    const handleStartPick = (next: Dayjs | null) => {
      const month = next ? next.startOf('month') : null;
      setDraft(([, currentEnd]) => {
        if (month && currentEnd && month.isAfter(currentEnd, 'month')) {
          return [month, null];
        }
        return [month, currentEnd];
      });
    };

    const handleEndPick = (next: Dayjs | null) => {
      const month = next ? next.startOf('month') : null;
      setDraft(([currentStart]) => [currentStart, month]);
    };

    const commit = (range: MonthYearRange) => {
      const [start, end] = range;
      onChange([
        start ? start.startOf('month') : null,
        end ? end.startOf('month') : null,
      ]);
      setOpen(false);
    };

    const handleApply = () => commit(draft);

    const handleShortcut = (shortcut: Shortcut) => {
      const next = shortcut.getValue();
      setDraft(next);
      commit(next);
    };

    const [draftStart, draftEnd] = draft;

    return (
      <VyThemeProvider>
        <FieldRoot
          ref={setRefs}
          type="button"
          isEmpty={isEmpty}
          disabled={disabled}
          onClick={handleOpen}
          data-vy={dataVy}
        >
          <Calendar width={ICON_SIZE} height={ICON_SIZE} />
          <span>{formatRange(value)}</span>
          <Calendar width={ICON_SIZE} height={ICON_SIZE} />
        </FieldRoot>
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <PopoverContent>
            <ShortcutsPanel>
              {SHORTCUTS.map((shortcut) => (
                <ShortcutButton
                  key={shortcut.label}
                  type="button"
                  onClick={() => handleShortcut(shortcut)}
                >
                  {shortcut.label}
                </ShortcutButton>
              ))}
            </ShortcutsPanel>
            <RightSide>
              <CalendarsRow>
                <DateCalendar
                  value={draftStart}
                  onChange={handleStartPick}
                  views={['month', 'year']}
                  openTo="month"
                  minDate={minDate}
                  maxDate={draftEnd ?? effectiveMaxDate}
                />
                <DateCalendar
                  value={draftEnd}
                  onChange={handleEndPick}
                  views={['month', 'year']}
                  openTo="month"
                  minDate={draftStart ?? minDate}
                  maxDate={effectiveMaxDate}
                />
              </CalendarsRow>
              <ActionsRow>
                <VyButton variant="text" onClick={handleClose}>
                  Cancel
                </VyButton>
                <VyButton variant="contained" onClick={handleApply}>
                  Apply
                </VyButton>
              </ActionsRow>
            </RightSide>
          </PopoverContent>
        </Popover>
      </VyThemeProvider>
    );
  },
);
